<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { attendancesApi, type AttendanceForm } from '@/api/attendances';
import { caregiversApi } from '@/api/caregivers';
import { patientsApi } from '@/api/patients';
import { shiftsApi, type ShiftTodayListItem } from '@/api/shifts';
import { useFilters } from '@/composables/useFilters';
import { useTableFilter } from '@/composables/useTableFilter';
import { usePermissions } from '@/composables/usePermissions';
import { useAccessibleCareers } from '@/composables/useAccessibleCareers';
import { useMobile } from '@/composables/useMobile';
import { useGroupFilterStore } from '@/stores/group-filter';
import { storeToRefs } from 'pinia';
import { getCareerColor, careers } from '@/utils/careers';
import GroupFilterSelect from '@/components/group-filter-select.vue';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import ShiftShowDialog from './shift-show-dialog.vue';
import ShiftTodayCard from './shift-today-card.vue';

const MS_IN_DAY = 86_400_000;
const PAD_WIDTH = 2;

const { hasPermission } = usePermissions();
const { canAccessCareer } = useAccessibleCareers();
const { isMobile, isPhone } = useMobile();

function sortByStatus(statusA: { name: string }, statusB: { name: string }) {
  const aName = statusA.name.toLowerCase();
  const bName = statusB.name.toLowerCase();

  return aName.localeCompare(bName);
}

const headers = [
  { title: 'Paciente', value: 'patientFullName', sortable: true },
  { title: 'Profesional', value: 'caregiverFullName', sortable: true },
  { title: 'Especialidad', value: 'caregiverHumanCareer', sortable: true },
  { title: 'Horario', value: 'humanTime', sortable: true },
  { title: 'Hora Inicio', value: 'checkIn.humanTime', sortable: true },
  { title: 'Hora Término', value: 'checkOut.humanTime', sortable: true },
  { title: 'Estado', value: 'status', sortable: true, sort: sortByStatus },
  { title: 'Acciones', value: 'actions', sortable: false },
];

const showIncidentsOnly = ref(false);

function capitalize(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
const search = ref('');
const currentDate = ref<Date>(new Date());
const DAY_NAME_INDEX = 0;
const MONTH_NAME_INDEX = 3;

const currentDateText = computed(() => {
  const words = currentDate.value.toLocaleDateString('es-CL', { dateStyle: 'full' }).split(' ');
  const capitalizeIndexes = [DAY_NAME_INDEX, MONTH_NAME_INDEX];

  return words
    .map((word, index) => (capitalizeIndexes.includes(index) ? capitalize(word) : word))
    .join(' ');
});

const { groupIds: groupFilterIds } = storeToRefs(useGroupFilterStore());

const filters = ref<{
  startDateTimeFilter: undefined | string
  offset: number
  patientGroupsIdIn: number[]
  light: string
}>({
  startDateTimeFilter: 'today',
  offset: 0,
  patientGroupsIdIn: groupFilterIds.value || [],
  light: 'true',
});

const selectedCareer = ref<string | undefined>(undefined);
const queryParams = useFilters(filters.value);

watch(() => filters.value.patientGroupsIdIn, (value) => {
  if (groupFilterIds.value !== value) groupFilterIds.value = value;
});
watch(groupFilterIds, (value) => {
  if (filters.value.patientGroupsIdIn !== value) {
    filters.value.patientGroupsIdIn = value;
  }
});

function updateOffset(offset: number) {
  filters.value.offset += offset;
  currentDate.value = new Date(currentDate.value.setDate(currentDate.value.getDate() + offset));
}

function resetOffset() {
  filters.value.offset = 0;
  currentDate.value = new Date();
}

function handleDateSelect(newDate: Date) {
  const selectedTime = newDate.setHours(0, 0, 0, 0);
  const todayTime = new Date().setHours(0, 0, 0, 0);

  const diffTime = selectedTime - todayTime;
  const daysOffset = Math.round((diffTime) / MS_IN_DAY);

  filters.value.offset = daysOffset;
  currentDate.value = new Date(selectedTime);
}

function padDatePart(value: number) {
  return String(value).padStart(PAD_WIDTH, '0');
}

// native <input type="date"> on phones works with local YYYY-MM-DD strings
const currentDateIso = computed(() => [
  currentDate.value.getFullYear(),
  padDatePart(currentDate.value.getMonth() + 1),
  padDatePart(currentDate.value.getDate()),
].join('-'));

function handleNativeDateSelect(value: string) {
  if (!value) return;

  const [year, month, day] = value.split('-').map(Number);
  // local-time construction avoids the UTC backshift of new Date('YYYY-MM-DD')
  handleDateSelect(new Date(year, month - 1, day));
}

const { data: shifts, refetch, isFetching } = useQuery({
  queryKey: ['todayShifts'],
  queryFn: () => shiftsApi.indexTodayList(queryParams.value),
});

const availableCareers = computed(() => {
  if (!shifts.value) return [];

  const uniqueCareers = [...new Set(shifts.value.map(s => s.caregiverCareer))];

  return careers.filter(c =>
    uniqueCareers.includes(c.value as typeof uniqueCareers[number]) && canAccessCareer(c.value));
});

function matchesActiveFilters(shift: ShiftTodayListItem) {
  if (showIncidentsOnly.value && !shift.hasIncidents) return false;
  if (selectedCareer.value && shift.caregiverCareer !== selectedCareer.value) return false;

  return true;
}

const filteredShifts = computed(() => {
  if (!shifts.value) return [];

  return shifts.value.filter(matchesActiveFilters);
});

const incidentsCount = computed(() => {
  if (!shifts.value) return 0;

  return shifts.value.filter(shift => shift.hasIncidents).length;
});

watch(filters, () => {
  selectedCareer.value = undefined;
  refetch();
}, { deep: true });

const cascadingGroupIds = computed(() => filters.value.patientGroupsIdIn);

useQuery({
  queryKey: ['patientsForFilters', cascadingGroupIds],
  queryFn: () => patientsApi.indexForFilters(cascadingGroupIds.value),
});

useQuery({
  queryKey: ['caregiversForFilters', cascadingGroupIds],
  queryFn: () => caregiversApi.indexForFilters(cascadingGroupIds.value),
});

const { mutate: createAttendance } = useMutation({
  mutationFn: (attendanceForm: AttendanceForm) => attendancesApi.create(attendanceForm),
  onSuccess: () => {
    refetch();
  },
});

const noDataText = computed(() => {
  if (search.value.trim()) {
    return 'No se encontraron turnos que coincidan con la búsqueda';
  }

  if (showIncidentsOnly.value) {
    return 'No hay turnos con incidentes para este día';
  }

  return 'No hay turnos asignados para este día';
});

const customFilter = useTableFilter([
  'patientFullName',
  'caregiverFullName',
  'caregiverHumanCareer',
  'humanTime',
  'checkIn.humanTime',
  'checkOut.humanTime',
  'status.name',
]);

// the card list applies the same search semantics the table gets via :search
const searchedShifts = computed(() => {
  const query = search.value?.trim();
  if (!query) return filteredShifts.value;

  return filteredShifts.value.filter((shift) => customFilter('', query, shift));
});

const canMarkAttendance = computed(() => hasPermission.value('attendances', 'create'));

const shiftShowDialogRef = ref<InstanceType<typeof ShiftShowDialog>>();

function openShiftDetail(item: ShiftTodayListItem) {
  shiftShowDialogRef.value?.open(item.id, {
    initialTab: (item.hasReports || item.hasIncidents) ? 'report' : 'general',
    initialReportTab: item.hasIncidents ? 'incidentRecords' : 'reportImages',
  });
}

// The medications email links here (?shift_id=…&date=YYYY-MM-DD&tab=medications):
// show that day and open the shift on its doses. The params are then dropped so a
// reload does not open it again.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MEDICATIONS_TAB = { initialTab: 'report', initialReportTab: 'medications' } as const;

function linkedShiftId(params: URLSearchParams) {
  const shiftId = Number(params.get('shift_id'));

  return Number.isInteger(shiftId) && shiftId > 0 ? shiftId : null;
}

function openLinkedShift() {
  const params = new URLSearchParams(window.location.search);
  const shiftId = linkedShiftId(params);
  if (!shiftId) return;

  const date = params.get('date');
  if (date && ISO_DATE.test(date)) handleNativeDateSelect(date);
  const options = params.get('tab') === 'medications' ? MEDICATIONS_TAB : undefined;
  shiftShowDialogRef.value?.open(shiftId, options);
  window.history.replaceState(window.history.state, '', window.location.pathname);
}

onMounted(openLinkedShift);

const showConfirmDialog = ref(false);
const pendingAttendance = ref<{
  shiftId: number
  caregiverId: number
  markType: 'check_in' | 'check_out'
  finish: boolean
  humanTime: string
} | null>(null);

// Marking the start of a shift that already ended finishes it in one step
// (start and end at the shift's times): offices that mark without the
// caregivers' app no longer need two marks per past shift.
function openConfirmDialog(shift: ShiftTodayListItem, markType: 'check_in' | 'check_out') {
  pendingAttendance.value = {
    shiftId: shift.id,
    caregiverId: shift.caregiverId,
    markType,
    finish: markType === 'check_in' && shift.hasEnded,
    humanTime: shift.humanTime,
  };
  showConfirmDialog.value = true;
}

function handleConfirm() {
  if (pendingAttendance.value) {
    const { shiftId, caregiverId, markType, finish } = pendingAttendance.value;
    createAttendance({ shiftId, caregiverId, markType, finish });
  }
  showConfirmDialog.value = false;
  pendingAttendance.value = null;
}

function handleCancel() {
  showConfirmDialog.value = false;
  pendingAttendance.value = null;
}

function handleConfirmDialogKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    event.preventDefault();
    handleConfirm();
  }
}

watch(showConfirmDialog, (isOpen) => {
  if (isOpen) {
    window.addEventListener('keydown', handleConfirmDialogKeydown);
  } else {
    window.removeEventListener('keydown', handleConfirmDialogKeydown);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleConfirmDialogKeydown);
});

const confirmDialogTitle = computed(() => {
  if (pendingAttendance.value?.finish) return 'Confirmar Turno Terminado';

  return pendingAttendance.value?.markType === 'check_in' ? 'Confirmar Inicio de Turno' : 'Confirmar Término de Turno';
});

const confirmDialogMessage = computed(() => {
  if (pendingAttendance.value?.finish) {
    return `Este turno (${pendingAttendance.value.humanTime}) ya terminó: se marcarán el inicio y el ` +
      'término en su horario y quedará como Terminado. ¿Deseas continuar?';
  }

  return pendingAttendance.value?.markType === 'check_in' ?
    '¿Estás seguro de que deseas marcar el inicio de este turno?' :
    '¿Estás seguro de que deseas marcar el término de este turno?';
});
</script>
<template>
  <v-card class="w-100">
    <v-data-table
      v-if="!isMobile"
      :headers="headers"
      :header-props="{ class: 'font-weight-bold' }"
      :items="filteredShifts"
      :search="search"
      :custom-filter="customFilter"
      :no-data-text="noDataText"
      :items-per-page="-1"
      multi-sort
    >
      <template #top>
        <v-toolbar
          color="white"
          flat
        >
          <v-toolbar-title class="font-weight-bold">
            Turnos
          </v-toolbar-title>
        </v-toolbar>
        <v-toolbar
          color="white"
          flat
        >
          <v-btn
            variant="outlined"
            size="small"
            class="ml-3"
            :disabled="isFetching"
            @click="resetOffset()"
          >
            Hoy
          </v-btn>
          <v-btn
            icon="mdi-chevron-left"
            :disabled="isFetching"
            @click="updateOffset(-1)"
          />
          <v-btn
            icon="mdi-chevron-right"
            :disabled="isFetching"
            @click="updateOffset(1)"
          />
          <div class="d-flex align-center">
            <p class="mr-4">
              {{ currentDateText }}
            </p>
            <v-menu
              :close-on-content-click="false"
            >
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon="mdi-calendar"
                  variant="text"
                  size="small"
                />
              </template>
              <v-date-picker
                v-model="currentDate"
                @update:model-value="handleDateSelect"
              />
            </v-menu>
          </div>
          <div class="ml-4 d-flex align-center ga-2">
            <v-chip
              v-if="incidentsCount > 0"
              size="small"
              :variant="showIncidentsOnly ? 'flat' : 'outlined'"
              :color="showIncidentsOnly ? 'error' : undefined"
              prepend-icon="mdi-alert"
              @click="showIncidentsOnly = !showIncidentsOnly"
            >
              Con incidentes ({{ incidentsCount }})
            </v-chip>
          </div>
          <v-spacer />
          <v-text-field
            v-model="search"
            label="Buscar"
            class="mx-4 mb-4 v-col-4"
            prepend-inner-icon="mdi-magnify"
            single-line
            variant="underlined"
            hide-details
          />
          <group-filter-select
            v-model="filters.patientGroupsIdIn"
            hide-details
            class="mb-4 mr-4 v-col-2"
          />
        </v-toolbar>
      </template>
      <template #header.caregiverHumanCareer="{ column }">
        <div class="d-flex align-center">
          {{ column.title }}
          <v-menu
            v-if="availableCareers.length > 1"
            :close-on-content-click="false"
          >
            <template #activator="{ props: menuProps }">
              <v-btn
                v-bind="menuProps"
                icon
                variant="text"
                size="x-small"
                class="ml-1"
              >
                <v-badge
                  :model-value="!!selectedCareer"
                  dot
                  :color="selectedCareer ? getCareerColor(selectedCareer) : undefined"
                >
                  <v-icon size="small">
                    mdi-filter-variant
                  </v-icon>
                </v-badge>
              </v-btn>
            </template>
            <v-card min-width="200">
              <v-list density="compact">
                <v-list-item
                  v-for="career in availableCareers"
                  :key="career.value"
                  :active="selectedCareer === career.value"
                  @click="selectedCareer = selectedCareer === career.value ? undefined : career.value"
                >
                  <template #prepend>
                    <v-icon
                      :color="getCareerColor(career.value)"
                      size="small"
                    >
                      mdi-circle
                    </v-icon>
                  </template>
                  <v-list-item-title>{{ career.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
              <v-divider v-if="selectedCareer" />
              <v-card-actions v-if="selectedCareer">
                <v-btn
                  size="small"
                  variant="text"
                  block
                  @click="selectedCareer = undefined"
                >
                  Limpiar filtro
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </div>
      </template>
      <template #item.patientFullName="{ item }">
        <v-tooltip
          :text="item.patientFullName"
          location="top"
        >
          <template #activator="{ props }">
            <span
              v-bind="props"
              class="truncated-name"
            >{{ item.patientFullName }}</span>
          </template>
        </v-tooltip>
      </template>
      <template #item.caregiverFullName="{ item }">
        <v-tooltip
          :text="item.caregiverFullName"
          location="top"
        >
          <template #activator="{ props }">
            <span
              v-bind="props"
              class="truncated-name"
            >{{ item.caregiverFullName }}</span>
          </template>
        </v-tooltip>
      </template>
      <template #item.caregiverHumanCareer="{ item }">
        <v-chip
          :color="getCareerColor(item.caregiverCareer)"
          size="small"
        >
          {{ item.caregiverHumanCareer }}
        </v-chip>
      </template>
      <template #item.status="{ item }">
        <v-chip :color="item.status.color">
          {{ item.status.name }}
        </v-chip>
      </template>
      <template #item.actions="{ item }">
        <v-btn
          icon="mdi-eye"
          size="small"
          :color="item.hasIncidents ? 'error' : item.hasReports ? 'primary' : undefined"
          :variant="(item.hasIncidents || item.hasReports) ? 'tonal' : 'elevated'"
          elevation="2"
          @click="openShiftDetail(item)"
        />
        <v-tooltip
          v-if="item.canCheckInByAdmin && hasPermission('attendances', 'create')"
          :text="item.hasEnded ? 'Marcar Inicio y Término' : 'Marcar Inicio'"
          location="top"
        >
          <template #activator="{ props }">
            <v-btn
              v-if="item.canCheckInByAdmin"
              v-bind="props"
              icon="mdi-location-enter"
              size="small"
              class="ml-2"
              @click="openConfirmDialog(item, 'check_in')"
            />
          </template>
        </v-tooltip>
        <v-tooltip
          v-if="item.canCheckOutByAdmin && hasPermission('attendances', 'create')"
          text="Marcar Término"
          location="top"
        >
          <template #activator="{ props }">
            <v-btn
              v-if="item.canCheckOutByAdmin"
              v-bind="props"
              icon="mdi-location-exit"
              size="small"
              class="ml-2"
              @click="openConfirmDialog(item, 'check_out')"
            />
          </template>
        </v-tooltip>
      </template>
    </v-data-table>

    <template v-else>
      <v-toolbar
        color="white"
        flat
      >
        <v-toolbar-title class="font-weight-bold">
          Turnos
        </v-toolbar-title>
      </v-toolbar>
      <div class="px-3 pb-3">
        <div class="d-flex align-center ga-1 mb-1">
          <v-btn
            variant="outlined"
            size="small"
            class="ml-3"
            :disabled="isFetching"
            @click="resetOffset()"
          >
            Hoy
          </v-btn>
          <v-btn
            icon="mdi-chevron-left"
            variant="text"
            size="small"
            :disabled="isFetching"
            @click="updateOffset(-1)"
          />
          <v-btn
            icon="mdi-chevron-right"
            variant="text"
            size="small"
            :disabled="isFetching"
            @click="updateOffset(1)"
          />
          <v-text-field
            v-if="isPhone"
            :model-value="currentDateIso"
            type="date"
            aria-label="Fecha"
            density="compact"
            variant="outlined"
            hide-details
            class="ml-1"
            @update:model-value="handleNativeDateSelect"
          />
          <v-menu
            v-else
            :close-on-content-click="false"
          >
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-calendar"
                variant="text"
                size="small"
              />
            </template>
            <v-date-picker
              v-model="currentDate"
              @update:model-value="handleDateSelect"
            />
          </v-menu>
        </div>
        <p class="text-body-2 text-medium-emphasis mb-2">
          {{ currentDateText }}
        </p>
        <v-chip
          v-if="incidentsCount > 0"
          size="small"
          class="mb-2"
          :variant="showIncidentsOnly ? 'flat' : 'outlined'"
          :color="showIncidentsOnly ? 'error' : undefined"
          prepend-icon="mdi-alert"
          @click="showIncidentsOnly = !showIncidentsOnly"
        >
          Con incidentes ({{ incidentsCount }})
        </v-chip>
        <v-text-field
          v-model="search"
          label="Buscar"
          prepend-inner-icon="mdi-magnify"
          density="compact"
          variant="outlined"
          hide-details
          class="mb-2"
        />
        <v-select
          v-if="availableCareers.length > 1"
          v-model="selectedCareer"
          :items="availableCareers"
          item-title="title"
          item-value="value"
          label="Especialidad"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          class="mb-2"
        />
        <group-filter-select
          v-model="filters.patientGroupsIdIn"
          hide-details
          density="compact"
          class="mb-3"
        />
        <v-progress-linear
          v-if="isFetching"
          indeterminate
          color="primary"
          class="mb-2"
        />
        <shift-today-card
          v-for="shift in searchedShifts"
          :key="shift.id"
          :shift="shift"
          :can-mark-attendance="canMarkAttendance"
          class="mb-2"
          @open="openShiftDetail(shift)"
          @check-in="openConfirmDialog(shift, 'check_in')"
          @check-out="openConfirmDialog(shift, 'check_out')"
        />
        <p
          v-if="!searchedShifts.length && !isFetching"
          class="text-center text-medium-emphasis py-8"
        >
          {{ noDataText }}
        </p>
      </div>
    </template>

    <shift-show-dialog
      ref="shiftShowDialogRef"
      @refetch="refetch"
    />

    <responsive-dialog
      v-model="showConfirmDialog"
      persistent
      max-width="400"
      :mobile-fullscreen="false"
    >
      <v-card>
        <v-card-title class="text-h6">
          {{ confirmDialogTitle }}
        </v-card-title>
        <v-card-text>
          {{ confirmDialogMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="error"
            variant="text"
            @click="handleCancel"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="handleConfirm"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </responsive-dialog>
  </v-card>
</template>
<style scoped>
.truncated-name {
  display: inline-block;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
}
</style>
